import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, FormControlLabel } from '@mui/material';
import { Page } from 'react-pdf';
import { LazyPDFPageProps } from './LazyPDFPage.props';
import {
  CustomTextLayerWrapper,
  HighlightableTextOverlayActionButton,
  PdfViewerPageContainer,
  PdfViewerPageHeader,
} from './LazyPDFPage.styles';
import CustomTextLayer from '../custom-text-layer/CustomTextLayer';
import HighlightLayer from '../highlight-layer/HighlightLayer';
import { getActiveCitationRectsForPage, mergeRects } from '../pdf-utils/rect-utils';
import { PDFViewerViewMode } from '../PDFViewer.props';

const LazyPDFPage = ({
  pageNumber,
  pageIndex,
  width,
  totalPages,
  allowPageSelection = false,
  allowNativeTextSelection = false,
  isSelected = false,
  onToggle,
  onRenderSuccess,
  pageChrome = 'header',
  viewMode = PDFViewerViewMode.DOCUMENT,
  pageData,
  nativeSelectionCopyFeedbackActive = false,
  activeCitation,
  activeCitationVariant = 'citation',
  activeTokenRefIndex = 0,
  forceVisible = false,
  selection,
  onCite,
  citationEnabled = false,
  searchHighlight,
}: LazyPDFPageProps) => {
  const [isVisible, setIsVisible] = useState(forceVisible);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (forceVisible) setIsVisible(true);
  }, [forceVisible]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setTimeout(() => setShouldAnimate(true), pageNumber * 50);
          observer.disconnect();
        }
      },
      { rootMargin: '100px', threshold: 0.1 }
    );

    if (pageRef.current) {
      observer.observe(pageRef.current);
    }
    return () => observer.disconnect();
  }, [pageNumber]);

  const handlePageRenderSuccess = useCallback(() => {
    onRenderSuccess?.(pageNumber);
  }, [onRenderSuccess, pageNumber]);

  const { focused: activeRects, dimmed: dimmedRects } = useMemo(
    () => getActiveCitationRectsForPage(activeCitation, pageData, activeTokenRefIndex),
    [activeCitation, pageData, activeTokenRefIndex]
  );

  const hasSelection = selection && selection.pageIndex === pageIndex;
  const selectionMergedRects = useMemo(
    () => (hasSelection ? mergeRects(selection!.rects) : []),
    [hasSelection, selection]
  );

  const addCitationButtonPos = useMemo(() => {
    if (!hasSelection || !pageData || selectionMergedRects.length === 0) return null;
    const scale = width / pageData.width;
    const lastRect = selectionMergedRects[selectionMergedRects.length - 1];
    return {
      top: (lastRect.top + lastRect.height) * scale + 6,
      left: (lastRect.left + lastRect.width / 2) * scale,
    };
  }, [hasSelection, pageData, selectionMergedRects, width]);
  const shouldRenderNativeTextLayer = allowNativeTextSelection && viewMode === PDFViewerViewMode.DOCUMENT && !pageData;
  const shouldRenderHeader = pageChrome === 'header' || allowPageSelection;

  return (
    <PdfViewerPageContainer
      ref={pageRef}
      id={`pdf-page-${pageNumber}`}
      data-pdf-page={pageNumber}
      className={shouldAnimate ? 'animate-in' : ''}
      $viewMode={viewMode}
      $pageChrome={pageChrome}
      $isVisible={isVisible}
      $isSelected={isSelected && allowPageSelection}
      $width={width}>
      {shouldRenderHeader && (
        <PdfViewerPageHeader>
          <span>
            Page {pageNumber} of {totalPages}
          </span>
          {allowPageSelection && (
            <FormControlLabel
              control={
                <Checkbox checked={isSelected} onChange={() => onToggle?.(pageNumber)} size="small" color="primary" />
              }
              label="Select for processing"
              labelPlacement="start"
            />
          )}
        </PdfViewerPageHeader>
      )}

      {isVisible && (
        <div style={{ position: 'relative' }}>
          <Page
            pageNumber={pageNumber}
            onRenderSuccess={handlePageRenderSuccess}
            renderTextLayer={shouldRenderNativeTextLayer}
            renderAnnotationLayer={false}
            width={width}
            scale={1}
          />

          {pageData &&
            dimmedRects.map((rects, i) => (
              <HighlightLayer
                key={`dimmed-${i}`}
                rects={rects}
                isFocused={false}
                pageWidth={pageData.width}
                renderedWidth={width}
                variant={activeCitationVariant}
              />
            ))}

          {activeRects && pageData && (
            <HighlightLayer
              rects={activeRects}
              isFocused
              pageWidth={pageData.width}
              renderedWidth={width}
              focusedId="active-citation-highlight"
              variant={activeCitationVariant}
            />
          )}

          {searchHighlight && searchHighlight.pageNumber === pageNumber && pageData && (
            <HighlightLayer
              rects={searchHighlight.rects}
              isFocused
              pageWidth={pageData.width}
              renderedWidth={width}
              focusedId="search-highlight"
              variant="search"
            />
          )}

          {hasSelection && pageData && (
            <HighlightLayer
              rects={selectionMergedRects}
              isFocused={false}
              pageWidth={pageData.width}
              renderedWidth={width}
            />
          )}

          {citationEnabled && hasSelection && addCitationButtonPos && (
            <HighlightableTextOverlayActionButton
              variant="contained"
              size="small"
              onClick={onCite}
              sx={{
                top: Math.max(8, addCitationButtonPos.top - 38),
                left: addCitationButtonPos.left,
              }}>
              Add Citation
            </HighlightableTextOverlayActionButton>
          )}

          {pageData && viewMode === PDFViewerViewMode.DOCUMENT && (
            <CustomTextLayerWrapper
              $showCopyFeedback={nativeSelectionCopyFeedbackActive}
              $showNativeSelection={allowNativeTextSelection && !citationEnabled}
              className="custom-text-layer"
              data-page-index={pageIndex}>
              <CustomTextLayer words={pageData.words} pageWidth={pageData.width} renderedWidth={width} />
            </CustomTextLayerWrapper>
          )}
        </div>
      )}
    </PdfViewerPageContainer>
  );
};

export default LazyPDFPage;
